import { Link } from "wouter";
import { BRAND, FOOTER_NAV } from "@/lib/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { Linkedin, Twitter, Youtube, Github } from "lucide-react";
import { submitForm, SubmitFormError } from "@/lib/submit-form";
import { track } from "@/lib/analytics";

const formSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

export function Footer() {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    setIsSubmitting(true);
    try {
      await submitForm("newsletter", values);
      track("marketing_form_submitted", { source: "newsletter" });
      toast({
        title: "Subscribed",
        description: "You've been added to our newsletter.",
      });
      form.reset();
    } catch (err) {
      if (err instanceof SubmitFormError && err.fieldErrors?.email) {
        form.setError("email", { type: "server", message: err.fieldErrors.email });
      }
      toast({
        title: "Subscription failed",
        description:
          err instanceof Error
            ? err.message
            : "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <footer className="bg-navy text-white pt-20 pb-10 border-t border-border/10">
      <div className="container mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-12 lg:gap-8 mb-16">
          <div className="lg:col-span-2 flex flex-col gap-6">
            <Link href="/" className="inline-block">
              <img
                src={`${import.meta.env.BASE_URL}brand/logo-lockup-white.svg`}
                alt={BRAND.name}
                className="h-8 w-auto"
              />
            </Link>
            <p className="text-slate-400 text-sm max-w-sm leading-relaxed">
              {BRAND.productLine}.<br />
              {BRAND.tagline}
            </p>
            <div className="flex items-center gap-4 mt-2">
              <a href={BRAND.social.linkedin} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                <Linkedin className="h-5 w-5" />
                <span className="sr-only">LinkedIn</span>
              </a>
              <a href={BRAND.social.x} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                <Twitter className="h-5 w-5" />
                <span className="sr-only">X (Twitter)</span>
              </a>
              <a href={BRAND.social.youtube} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                <Youtube className="h-5 w-5" />
                <span className="sr-only">YouTube</span>
              </a>
              <a href={BRAND.social.github} target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-white transition-colors">
                <Github className="h-5 w-5" />
                <span className="sr-only">GitHub</span>
              </a>
            </div>
          </div>

          {FOOTER_NAV.map((section) => (
            <div key={section.title} className="flex flex-col gap-4">
              <h4 className="text-sm font-semibold text-white uppercase tracking-wider">
                {section.title}
              </h4>
              <ul className="flex flex-col gap-3">
                {section.items.map((item) => (
                  <li key={item.label}>
                    <Link href={item.path || "#"} className="text-sm text-slate-400 hover:text-cyan transition-colors">
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-sm text-slate-500">
            &copy; {new Date().getFullYear()} {BRAND.name}, Inc. All rights reserved.
          </div>
          
          <div className="flex items-center gap-4 w-full md:w-auto">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex items-center gap-2 w-full md:w-auto">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem className="flex-1 md:w-64 space-y-0">
                      <FormControl>
                        <Input 
                          placeholder="Subscribe to our newsletter" 
                          className="bg-white/5 border-white/10 text-white placeholder:text-slate-500 h-9 text-sm focus-visible:ring-cyan"
                          {...field} 
                        />
                      </FormControl>
                      <FormMessage className="text-xs absolute mt-1" />
                    </FormItem>
                  )}
                />
                <Button type="submit" size="sm" disabled={isSubmitting} className="h-9 bg-white text-navy hover:bg-slate-200">
                  {isSubmitting ? "Subscribing..." : "Subscribe"}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </footer>
  );
}
